<script setup lang="ts">
import AudiencePreview from '@/components/audiences/AudiencePreview.vue'
import Email from '@/components/email/Email.vue'

import type { SelectedAudience } from '@/components/audiences/Audience.vue'

const count = defineModel<number>('count')
const isLoadingCount = defineModel<boolean>('isLoadingCount')
const step = defineModel<number>('step')
const emailContentId = defineModel<number | null>('emailContentId')
const selectedAudience = defineModel<SelectedAudience>('selectedAudience')

defineProps<{
  name: string
  selectedTemplateName: string | null
  userStoresAudience: number
  userStoresCount: number
}>()

const { t } = useI18n()

const emailComponent = ref<InstanceType<typeof Email>>()

const selectedEmailTemplate = computed(() => (emailContentId.value ? String(emailContentId.value) : null))

const handleEmailTemplateSaved = (data: { templateId: string; templateName?: string }) => {
  emailContentId.value = Number(data.templateId)
}

const handleTemplateSelectorSelect = (data: { templateId: string }) => {
  emailContentId.value = Number(data.templateId)
}

const handleChangeTemplate = () => {
  emailContentId.value = null
  emailComponent.value?.openTemplateSelector()
}
</script>

<template>
  <div class="flex w-full flex-col gap-6 pb-6 xl:grid xl:grid-cols-12 px-12">
    <section class="flex flex-col gap-6 col-span-8">
      <div class="rounded-md border bg-white">
        <div class="w-full border-b p-6">
          <h2 class="text-base font-semibold sm:text-lg">
            {{ t('pages.campaignEmail.create.steps.step2Title') }}
          </h2>
        </div>
        <div class="flex flex-col gap-4 p-6">
          <div
            :class="[
              'dotted-background bg-content-bg flex items-center justify-center rounded-md border border-border',
              {
                'p-0': selectedEmailTemplate,
                'p-8': !selectedEmailTemplate
              }
            ]"
          >
            <Email
              ref="emailComponent"
              :selected-email-template="selectedEmailTemplate"
              @handle-email-template-saved="handleEmailTemplateSaved"
              @handle-template-selector-select="handleTemplateSelectorSelect"
            />
          </div>

          <div v-if="selectedEmailTemplate" class="flex gap-2">
            <Button
              variant="outline"
              class="flex-1 bg-white text-foreground border-border hover:bg-accent"
              @click="emailComponent?.openEmailEditor()"
            >
              <BaseIcon name="HeroPencilSquareIconOutline" class="h-4 w-4" />
              {{ t('pages.marketingFlow.marketingFlowEditPage.action.editEmailTemplate') }}
            </Button>

            <Button
              variant="outline"
              class="flex-1 bg-white text-foreground border-border hover:bg-accent"
              @click="handleChangeTemplate"
            >
              <BaseIcon name="HeroArrowPathIconOutline" class="h-4 w-4" />
              {{ t('pages.campaignEmail.create.changeTemplate') }}
            </Button>
          </div>
        </div>
      </div>
    </section>

    <section class="flex flex-col gap-6 col-span-4">
      <AudiencePreview
        class="sticky top-2"
        :rules="{}"
        :targets-count="count"
        :stores-contacts-count="userStoresAudience"
        :stores-count="userStoresCount"
        :selected-audience="selectedAudience"
        :loading="isLoadingCount"
      >
        <template #title>
          <div class="flex items-center justify-between">
            <p class="text-base font-semibold text-foreground">
              {{ t('pages.campaignEmail.create.preview.title') }}
            </p>
            <Button variant="default-primary" :disabled="!emailContentId" @click="step = step + 1">
              {{ t('pages.campaignEmail.create.preview.buttons') }}
            </Button>
          </div>
        </template>

        <template #default>
          <div v-if="selectedTemplateName" class="mt-4 flex items-center gap-2 rounded-md bg-primary/5 p-3">
            <BaseIcon name="HeroEnvelopeIconSolid" class="h-4 w-4 text-primary" />
            <span class="text-sm text-primary">{{ selectedTemplateName }}</span>
          </div>
        </template>
      </AudiencePreview>
    </section>
  </div>
</template>
